import ExcelJS from "exceljs";
import { customInstance } from "../../api/custom-instance";
import { convertNumberToDecimal, downloadData, formatTruthTableData, getAtomOrBondColors, getConformalValue, getStatsCellColor, modelOverlayLayout, rocLayout, scatterPlotLayout } from "../../utils/formattingFns";
import { Dataset, Model, ModelFamily, Prediction, Protocol, ProtocolMetadataOutputStageChanges, ProtocolMetadataOutputTotalChanges, ReadAcross, Resultset, StageChanges } from "../../api/models";
import { TableData, TableDataField, TableHeader } from "../../components/Table/DataTable";
import { modelStatLimits } from "../../utils/supplementalData";
import Plotly from 'plotly.js-dist-min';
import { formatBatchPredictionMetadata, formatDatasetDataframe, formatDatasetMetadata, formatModelData, formatModelMetadata, formatProtocolStageMetadata, formatReadacrossData, formatReadacrossMetadata, formatResultsetDataframe, formatResultsetValidationsData, formatResultsetVisualizationMetadata, formatVisualizationData } from "../../utils/dataFormattingFns";
import { ResultsetEvaluationMetadata, ResultsetGrouping } from "../../routes/Resultsets/pages/IndividualResultset";
import { ReadAcrossDataType } from "../../routes/ReadAcross/page/IndividualReadAcrossPage";
import { ResultsetVisualizationType } from "../../routes/Resultsets/components/ResultsetVisualizations";

const application = process.env.REACT_APP_APPLICATION ?? '';

// Temporary export reduction toggle: keep molecule highlighting payload to permutation only in XLSX.
const XLSX_EXPORT_PERMUTATION_ONLY_HIGHLIGHTING = true;
const XLSX_EXPORT_HIGHLIGHTING_METHOD_FILTER = 'permutation';
const XLSX_EXPORT_INCLUDE_MODEL_FAMILY_DATASET_METADATA = true;

export interface ResultsetDownloadItem {
    modelFamily: ModelFamily,
    data: TableData[],
    headers: TableHeader[],
    methodHeaders: TableHeader[],
    highlightingHeaders: TableHeader[],
    isConformalPredictor: boolean,
}

export interface ResultsetVisualizationDownloadFormat {
    structure?: string,
    smiles?: string,
    x: number,
    y: number,
}

const MIN_CELL_WIDTH = 20;
const HEADER_ROW_FILL: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
const DARK_CELL_FILL: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B7B7B' } };

const useDownload = () => {

    const downloadDatasetProtocol = async (
        dataset: Dataset,
        protocol: Protocol,
    ) => {
        try {
            await createDatasetProtocolExcelFile(dataset, protocol);
        } catch (err:any) {
            console.log(err);
        }
    }

    const downloadDataset = async (
        ext: string,
        dataset: Dataset,
        dataframe?: { [key: string]: { [key: number]: string | number | null } },
    ) => {
        const { name, file_hash } = dataset;

        const url = `dataset/download/${file_hash}?file_type=${ext}`;

        try {
            if (ext !== '.xlsx') {
                const response = await customInstance({
                    url,
                    responseType: 'blob',
                    method: 'GET',
                });

                downloadData(name ?? '', ext, response);
            } else {
                let df = dataframe;

                if (!df) {
                    const response = await customInstance({
                        url: `dataset/dataframe/${file_hash}`,
                        method: 'GET',
                    });
                    df = response as unknown as { [key: string]: { [key: number]: string | number | null } };
                }
                await createDatasetExcelFile(dataset, df);
            }
        } catch (err:any) {
            console.log(err);
        }
    }

    const downloadBatchPrediction = async (
        ext: string,
        resultset: Resultset,
        groupedResultsets?: ResultsetGrouping[],
        threshold?: number,
        predictions?: Prediction[],
        rsEvalMetadata?: ResultsetEvaluationMetadata[],
        useProba: boolean = false,
    ) => {
        const { name, id } = resultset;
        const url = `resultset/download/${id}?file_type=${ext}&applicability_domain=true&include_duplicates=true&include_duplicate_values=true`;

        try {
            if (ext !== '.xlsx') {
                const response = await customInstance({
                    url,
                    responseType: 'blob',
                    method: 'GET',
                });

                downloadData(name ?? '', ext, response);
            } else {
                let groups: ResultsetGrouping[] | undefined = groupedResultsets;
                let resultsetPredictions: Prediction[] | undefined = predictions;
                let resultsetEvaluations: ResultsetEvaluationMetadata[] | undefined = rsEvalMetadata;
                const shouldRefetchForXlsx = XLSX_EXPORT_PERMUTATION_ONLY_HIGHLIGHTING
                    || groupedResultsets === undefined
                    || predictions === undefined
                    || rsEvalMetadata === undefined;

                if (shouldRefetchForXlsx) {
                    const predResponse = await customInstance({
                        url: `resultset/predictions/${resultset.id}`,
                        method: 'GET',
                    });

                    const predictions = predResponse as unknown as Prediction[];

                    // Fetch base dataframe WITHOUT molecule highlighting (fast, small payload)
                    const response = await customInstance({
                        url: `resultset/dataframe_v2/${id}?applicability_domain=true&include_duplicates=true&include_duplicate_values=true&include_molecule_highlighting=false`,
                        method: 'GET',
                    });

                    const dataframe = response as unknown as { [key: string]: any };

                    // Fetch molecule highlighting separately via dedicated endpoint
                    const mhMethodParam = XLSX_EXPORT_PERMUTATION_ONLY_HIGHLIGHTING
                        ? `&method_filter=${XLSX_EXPORT_HIGHLIGHTING_METHOD_FILTER}`
                        : '';
                    const mhResponse = await customInstance({
                        url: `resultset/molecule_highlighting/${id}?${mhMethodParam}`,
                        method: 'GET',
                    });
                    const mhData = mhResponse as unknown as { [modelHash: string]: { molecule_highlighting: any[] } };

                    // Merge MH data into dataframe shape for formatResultsetDataframe compatibility
                    if (mhData && dataframe.model_hash) {
                        for (const [modelHash, mhPayload] of Object.entries(mhData)) {
                            if (dataframe.model_hash[modelHash]) {
                                dataframe.model_hash[modelHash].molecule_highlighting = mhPayload.molecule_highlighting;
                            }
                        }
                    }

                    const { groupedRsData, predEvalMetadata } = await formatResultsetDataframe(
                        dataframe,
                        predictions,
                        useProba,
                        {
                            moleculeHighlightingMethodFilter: XLSX_EXPORT_PERMUTATION_ONLY_HIGHLIGHTING
                                ? XLSX_EXPORT_HIGHLIGHTING_METHOD_FILTER
                                : undefined,
                            includeFeatureImportanceMethodInHighlightingLabel: true,
                        },
                    );
                    
                    groups = groupedRsData;
                    resultsetEvaluations = predEvalMetadata;
                    resultsetPredictions = predictions;
                }

                if (groups !== undefined && resultsetPredictions !== undefined) {
                    await createResultsetExcelFile(resultset, groups, resultsetPredictions, threshold, resultsetEvaluations);
                }
                
                
            }
        } catch (err:any) {
            console.log(err);
        }
    };

    // const downloadBatchPrediction = async (
    //     ext: string,
    //     resultset: Resultset,
    //     groupedResultsets?: ResultsetGrouping[],
    //     threshold?: number,
    //     predictions?: Prediction[],
    // ) => {
    //     const { name, id } = resultset;
    //     const url = `resultset/download/${id}?file_type=${ext}&applicability_domain=true&include_duplicates=true&include_duplicate_values=true`;

    //     try {
    //         if (ext !== '.xlsx') {
    //             const response = await customInstance({
    //                 url,
    //                 responseType: 'blob',
    //                 method: 'GET',
    //             });

    //             downloadData(name ?? '', ext, response);
    //         } else {
    //             let groups: ResultsetGrouping[] | undefined = groupedResultsets;
    //             let resultsetPredictions: Prediction[] | undefined = predictions;

    //             if (groups === undefined) {
    //                 const response = await customInstance({
    //                     url: `resultset/dataframe_v2/${id}?applicability_domain=true&include_duplicates=true&include_duplicate_values=true&include_molecule_highlighting=true`,
    //                     method: 'GET',
    //                 });

    //                 const dataframe = response as unknown as { [key: string]: any };
    //                 const { groupedRsData } = await formatResultsetDataframe(dataframe);
    //                 groups = groupedRsData;
    //             }

    //             if (resultsetPredictions === undefined) {
    //                 const predResponse = await customInstance({
    //                     url: `resultset/predictions/${resultset.id}`,
    //                     method: 'GET',
    //                 });

    //                 resultsetPredictions = predResponse as unknown as Prediction[];
    //             }
                
    //             await createResultsetExcelFile(resultset, groups, resultsetPredictions, threshold);
                
    //         }
    //     } catch (err:any) {
    //         console.log(err);
    //     }
    // };

    const downloadResultsetVisualization = async (
        ext: string,
        visualization: ResultsetVisualizationType,
        resultset: Resultset,
    ) => {
        const hash = visualization.metadata.file_hash;
        const methodType = visualization.metadata.dataset_visualization_metadata.visualization_method_metadata.method_type;
        const { name: resultset_name, id: resultset_id } = resultset;

        const url = `resultset/dataset_visualization_download/${resultset_id}/${hash}?include_smiles=${application !== 'assay-central' ? 'false' : 'true'}&file_type=${ext}`;
        const name = `${resultset_name ? resultset_name.split('.')[0] : 'Resultset'}_${methodType}`;

        try {   
            if (ext !== '.xlsx') {
                const response = await customInstance({
                    url,
                    responseType: 'blob',
                    method: 'GET',
                });

                downloadData(name ?? '', ext, response);
            } else {
                const visData = await formatVisualizationData(visualization, application === 'assay-central' ? true : false);
                await createVisualizationExcelFile(name, visualization, resultset, visData);
            }
        } catch (err:any) {
            console.log(err);
        }

    }

    const downloadModelFamilyStats = async (
        ext: string,
        modelFamily: ModelFamily,
        models?: Model[],
        data?: TableData[],
    ) => {
        try {
            if (ext === '.xlsx') {
                let modelFamilyModels = models;
                let tableData = data;

                if (modelFamilyModels === undefined && tableData === undefined) {
                    const response = await customInstance({
                        url: `model_family/models/${modelFamily.id!}`,
                    });
                    const modelsData = response as unknown as Model[];
                    modelFamilyModels = modelsData;

                    const { modelData } = formatModelData(modelsData);
                    tableData = modelData;
                    await new Promise(resolve => setTimeout(resolve, 0));
                }
 
                if (modelFamilyModels && tableData) {
                    await createModelFamilyStatsExcelFile(modelFamily, modelFamilyModels, tableData);
                }

            }
        } catch (err:any) {
            console.log(err);
        }
    }

    const downloadModelStats = async (
        ext: string,
        model: Model,
        data: TableData,
    ) => {

        const methodField = data.fields.find(field => field.associatedColumn === 'method');
        const methodName = methodField ? (methodField.value as string) : '';
        const modelFamilyName = model.name ?? '';
        
        const url = `model/download/${model.file_hash}`;
        const name = `${modelFamilyName.split('.')[0]}_${methodName}`;

        try {
            if (ext !== '.xlsx') {
                const response = await customInstance({
                    url,
                    responseType: 'blob',
                    method: 'GET',
                });

                downloadData(name, ext, response);
            } else {
                if (data) {
                    await createModelStatsExcelFile(modelFamilyName, methodName, data, model);
                }
            }
        } catch (err:any) {
            console.log(err);
        }
    }

    const downloadReadacrossData = async (
        ext: string,
        readacross: ReadAcross,
        data?: ReadAcrossDataType[],
    ) => {
        try {
            let raData = data;

            if (raData === undefined) {
                const response = await customInstance({
                    url: `readacross/dataframe/${readacross.id}`,
                    method: 'GET',
                });

                const dataframe = response as unknown as {[key: string]: { [key: number]: string | number }};
                if (window.RDKit) {
                    raData = formatReadacrossData(dataframe, window.RDKit);
                }
            };

            await createReadacrossExcelFile(readacross, raData ?? []);
        } catch (err:any) {
            console.log(err);
        }
    }

    const createModelFamilyStatsExcelFile = async (
        modelFamily: ModelFamily,
        models: Model[],
        data: TableData[],
    ) => {
        const { 
            name: modelFamilyName,
            created_by,
            creation_date,
            id,
            owner,
            updated_date, 
        } = modelFamily;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(`${modelFamilyName ?? ''} Metrics`);

        worksheet.columns = Array.from(Array(16).keys()).map(key => ({ width: 22 }));

        const topRows = [
            ['Model Family Name', modelFamilyName ?? ''],
            ['Model Family ID', id ?? ''],
            [''],
            ['created_by', created_by ?? ''],
            ['owner', owner ?? ''],
            ['creation_date', creation_date ? new Date(creation_date).toLocaleString('en-US', {timeZone: 'EST'}) : ''],
            ['updated_date', updated_date ? new Date(updated_date).toLocaleString('en-US', {timeZone: 'EST'}) : ''],
            [''],
            ['Model Metrics'],
            ['']
        ];

        topRows.forEach((rowValues, index) => {
            const row = worksheet.getRow(index + 1);
            row.height = 16;
            row.values = rowValues;
            row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };

            if (index === topRows.length - 1) {
                row.border = { bottom: { style: 'thick',color: { argb: 'FF000000' } } }
            }

            row.eachCell((cell) => {
                if (cell.value && ['Model Family Name', 'Model Family ID', 'Model Metrics'].includes(cell.value.toString())) {
                    cell.font = { bold: true };
                }
            });
        });

        const statsHeadingRowIndex = topRows.length + 1;
        const statsHeadingRow = worksheet.getRow(statsHeadingRowIndex);
        statsHeadingRow.height = 16;

        statsHeadingRow.getCell(1).value = 'Method Name';
        const representativeRow = data[0].fields.filter(field => field.type !== 'plot' && field.associatedColumn !== 'method');
        worksheet.mergeCells(statsHeadingRowIndex, 2, statsHeadingRowIndex, 2+representativeRow.length-1);
        statsHeadingRow.getCell(2).value = 'Statistics';

        [1,2].forEach((cellNum) => {
            const cell = statsHeadingRow.getCell(cellNum);
            cell.font = { bold: true };
            cell.alignment = { vertical: 'middle', horizontal: 'left' }
            if (cellNum === 2) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B7B7B' } };
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            }
        });

        const statsRow = worksheet.getRow(statsHeadingRowIndex+1);
        statsRow.values = [''].concat(representativeRow.map(rep => rep.associatedColumn));
        statsRow.font = { bold: true };
        statsRow.alignment = { vertical: 'middle', horizontal: 'center' };

        const dataRowIndex = statsHeadingRowIndex+2;

        data.forEach((modelData, i) => {
            const modelDataRow = worksheet.getRow(dataRowIndex+i);
            modelDataRow.height = 16;
            const fields = modelData.fields.filter(field => field.type !== 'plot');
            fields.forEach((field, cellIndex) => {
                const { value, associatedColumn, type } = field;
                const cell = modelDataRow.getCell(cellIndex+1);
                if (type === 'stat') {
                    cell.value = convertNumberToDecimal(value as string | number);
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };

                    const modelLimit = modelStatLimits.find(limit => limit.value === associatedColumn);
                    let color = '#E0E0E0';
                    if (modelLimit) {
                        color = getStatsCellColor({ min: modelLimit.min, max: modelLimit.max }, Number(value), false);
                    } 

                    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${color.split('#')[1]}` } };
                } else {
                    cell.value = value as string | number;
                    cell.alignment = { vertical: 'middle', horizontal: associatedColumn === 'method' ? 'left' : 'center' };
                }
            })
        });

        const plotRowIndex = dataRowIndex + data.length;

        for (const [i, modelData] of Object.entries(data)) {
            const rowIndex = Number(i) + plotRowIndex + 1;

            const fields = modelData.fields.filter(field => field.type === 'plot');
            const row = worksheet.getRow(rowIndex);
            row.height = 320;

            const nameField = modelData.fields.find(field => field.associatedColumn === 'method');
            if (nameField) {
                const nameCell = row.getCell(1);
                nameCell.value = nameField.value as string;
                nameCell.font = { bold: true };
                nameCell.alignment = { vertical: 'middle', horizontal: 'left' }; 
            }

            const plotCells: {
                startCol: number,
                endCol: number,
                plotField: TableDataField,
            }[] = fields.map((field, fieldIndex) => {
                let startCol = 0;
                let endCol = 0

                if (fieldIndex === 0) {
                    startCol = 2;
                    endCol = 4;
                } else if (fieldIndex === 1) {
                    startCol = 5;
                    endCol = 7;
                } else {
                    startCol = 8;
                    endCol = 10;
                }

                return {
                    startCol,
                    endCol,
                    plotField: field,
                }
            });

            for (const plotCell of plotCells) {
                const { startCol, endCol, plotField } = plotCell;
                worksheet.mergeCells(rowIndex, startCol, rowIndex, endCol);

                let plotData = plotField.value;
                let layout = null;

                if (plotField.associatedColumn === 'roc') {
                    layout = rocLayout;
                } else if (plotField.associatedColumn === 'overlay') {
                    layout = modelOverlayLayout;
                } else if (plotField.associatedColumn === 'truthTable') {
                    const formattedData = formatTruthTableData(plotField.value as any[]);
                    plotData = formattedData.traces;
                    layout = formattedData.layout;
                } else {
                    layout = scatterPlotLayout(plotField.value as any[]);
                };

                const url = await Plotly.toImage({
                    data: plotData as any[],
                    layout: layout as any,
                }, { width: 500, height: 500, format: 'png' });

                if (url) {
                    const imageToAdd = workbook.addImage({
                        base64: url,
                        extension: 'png',
                    });

                    worksheet.addImage(imageToAdd, {
                        tl: { col: startCol-1+0.1, row: rowIndex-1+0.1 },
                        ext: { width: 500, height: 500 },
                    });
                }
            }
        };

        for (const model of models) {
            const matchingData = data.find(d => d.id === model.file_hash!);
            if (matchingData) {
                const methodNameField = matchingData.fields.find(field => field.associatedColumn === 'method');
                if (methodNameField) {  
                    const methodName = methodNameField.value as string;
                    const modelMetadata = await formatModelMetadata(model, methodName);
                    if (modelMetadata.length > 0) {
                        const metadataWorksheet = workbook.addWorksheet(`${methodName} Model Metadata`);

                        modelMetadata.forEach((metadata, index) => {
                            const row = metadataWorksheet.getRow(index + 1);
                            row.height = 16;
                            row.values = metadata

                            if (index <= 5) {
                                row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
                                if (index === 5) {
                                    row.border = { bottom: { style: 'thick', color: { argb: 'FF000000' } } };
                                }
                            } 

                            row.eachCell((cell) => {
                                const boldValues = ['Training File','Model Family', 'Method', 'Model Metadata', 'Fingerprint Metadata', 'Nested CV Metadata', 'Training Metadata']
                                if (cell.value && boldValues.includes(cell.value as string)) {
                                    cell.font = { bold: true }
                                }
                            });
                        });

                        let colWidth = 0;
                        const firstCol = metadataWorksheet.getColumn(1);

                        firstCol.eachCell((cell) => {
                            if (cell.value) {
                                const length = cell.value.toLocaleString().length;
                                if (length > colWidth) {
                                    colWidth = length;
                                }
                            }
                        });

                        firstCol.width = colWidth;
                    };
                };
            };  
        };

        const buffer = await workbook.xlsx.writeBuffer();
        downloadData(modelFamily.name ?? '', '.xlsx', buffer);

    }

    const createModelStatsExcelFile = async (
        modelFamilyName: string,
        methodName: string,
        data: TableData,
        model: Model,
    ) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(`${modelFamilyName} Metrics`);

        const fields = data.fields;
        const stats = fields.filter(field => field.type === 'stat');
        const plots = fields.filter(field => field.type === 'plot');

        worksheet.columns = Array.from(Array(10).keys()).map(key => ({ width: 22 }));

        const topRows = [
            ['Method Name', methodName],
            ['Model Family Name', modelFamilyName],
            [''],
            ['Model Metrics'],
            ['']
        ];

        topRows.forEach((rowValues, index) => {
            const row = worksheet.getRow(index + 1);
            row.height = 16;
            row.values = rowValues;
            row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };

            if (index === topRows.length - 1) {
                row.border = { bottom: { style: 'thick',color: { argb: 'FF000000' } } }
            }

            row.getCell(1).font = { bold: true }
        });

        const startingRowIndex = topRows.length + 1;

        const statsHeadingRow = worksheet.getRow(startingRowIndex);
        const statsValueRow = worksheet.getRow(startingRowIndex+1);

        statsHeadingRow.border = { bottom: { style: 'thin', color: { argb: 'FF000000' } } };
        statsHeadingRow.height = 16;
        statsValueRow.height = 16;

        stats.forEach((stat, i) => {
            const labelCell = statsHeadingRow.getCell(i+1);
            labelCell.value = stat.associatedColumn;
            labelCell.font = { bold: true };
            labelCell.alignment = { vertical: 'middle', horizontal: 'center' };

            const valueCell = statsValueRow.getCell(i+1);
            valueCell.value =  stat.value !== '' ? convertNumberToDecimal(Number(stat.value)) : stat.value;
            valueCell.alignment = { vertical: 'middle', horizontal: 'center' };
            const modelLimit = modelStatLimits.find(limit => limit.value === stat.associatedColumn);
            let color = '#E0E0E0';
            if (modelLimit && stat.value !== '') {
                color = getStatsCellColor({ min: modelLimit.min, max: modelLimit.max }, Number(stat.value), false);
            } 
            valueCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${color.split('#')[1]}` } };
        });

        const chartsRowIndex = startingRowIndex + 3;
        const chartsRow = worksheet.getRow(chartsRowIndex);
        chartsRow.height = 320;
        const plotCells = plots.map((plot, i) => {
            let startCell = `A${chartsRowIndex}`;
            let endCell = `A${chartsRowIndex}`;
            let col = 0;

            if (i === 0) {
                endCell = `C${chartsRowIndex}`
            } else if (i === 1) {
                startCell = `D${chartsRowIndex}`;
                endCell = `F${chartsRowIndex}`;
                col = 3;
            } else if (i === 2) {
                startCell = `G${chartsRowIndex}`;
                endCell = `I${chartsRowIndex}`;
                col = 6;
            };

            return {
                plot,
                startCell,
                endCell,
                col
            }
        });

        const plotUrls: { url: string | undefined, col: number }[] = [];

        const promises = plotCells.map(async(plotCell) => {
            worksheet.mergeCells(`${plotCell.startCell}:${plotCell.endCell}`);
            let data = plotCell.plot.value;
            let layout = null;

            if (plotCell.plot.associatedColumn === 'roc') {
                layout = rocLayout;
            } else if (plotCell.plot.associatedColumn === 'overlay') {
                layout = modelOverlayLayout;
            } else if (plotCell.plot.associatedColumn === 'truthTable') {
                const formattedData = formatTruthTableData(plotCell.plot.value as any[]);
                data = formattedData.traces;
                layout = formattedData.layout;
            } else {
                layout = scatterPlotLayout(data as any[]);
            }

            const url = await Plotly.toImage({
                data: data as any[],
                layout: layout as any,
            }, { width: 500, height: 500, format: 'png' });
            return plotUrls.push({ url, col: plotCell.col });
        });

        await Promise.all(promises);
        
        if (plotUrls.length > 0) {
            plotUrls.forEach(plotUrl => {
                if (plotUrl.url) {
                    const imageToAdd = workbook.addImage({
                        base64: plotUrl.url,
                        extension: 'png',
                    });

                    worksheet.addImage(imageToAdd, {
                        tl: { col: Number(plotUrl.col)+0.1, row: chartsRowIndex-1+0.1 },
                        ext: { width: 500, height: 500 },
                    });
                }
            })
        }

        const modelMetadata = await formatModelMetadata(model, methodName);
        if (modelMetadata.length > 0) {
            const metadataWorksheet = workbook.addWorksheet(`${methodName} Model Metadata`);

            modelMetadata.forEach((metadata, index) => {
                const row = metadataWorksheet.getRow(index + 1);
                row.height = 16;
                row.values = metadata

                if (index <= 5) {
                    row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
                    if (index === 5) {
                        row.border = { bottom: { style: 'thick', color: { argb: 'FF000000' } } };
                    }
                } 

                row.eachCell((cell) => {
                    const boldValues = ['Training File','Model Family', 'Method', 'Model Metadata', 'Fingerprint Metadata', 'Nested CV Metadata', 'Training Metadata']
                    if (cell.value && boldValues.includes(cell.value as string)) {
                        cell.font = { bold: true }
                    }
                });
            });

            let colWidth = 0;
            const firstCol = metadataWorksheet.getColumn(1);

            firstCol.eachCell((cell) => {
                if (cell.value) {
                    const length = cell.value.toLocaleString().length;
                    if (length > colWidth) {
                        colWidth = length;
                    }
                }
            });

            firstCol.width = colWidth;
        }


        const buffer = await workbook.xlsx.writeBuffer();
        downloadData(`${modelFamilyName}_${methodName}`, '.xlsx', buffer);
    }


    const createResultsetExcelFile = async (
        resultset: Resultset, 
        groupedResultsets: ResultsetGrouping[], 
        predictions: Prediction[],
        threshold?: number,
        predEvalMetadata?: ResultsetEvaluationMetadata[],
    ) => {
        const ROW_HEIGHT = 107;
        const COL_WIDTH = 16;

        const workbook = new ExcelJS.Workbook();

        for (const [groupIndex, group] of Object.entries(groupedResultsets)) {
            const groupNum = Number(groupIndex);
            const { 
                modelFamilyId, 
                modelFamilyName, 
                metricsHeaders, 
                modelHeaders, 
                moleculeHighlightingHeaders,
                isConformal,
                data 
            } = group;

            const worksheet = workbook.addWorksheet(`${groupNum+1} - ${modelFamilyName} Prediction Stats`);
            const allHeaders = metricsHeaders.concat(modelHeaders).concat(moleculeHighlightingHeaders);

            allHeaders.forEach((header, i) => {
                const column = worksheet.getColumn(i+1);
                let width = COL_WIDTH;

                if (i > 0) {
                    if (header.value === 'smiles') {
                        width = 28;
                    } else {
                        width = Math.max(MIN_CELL_WIDTH, header.label.toString().length) + 4;
                    }
                };

                column.width = width;
            });

            let startingRowIndex = 1;

            if (isConformal) {
                const firstRow = worksheet.getRow(1);
                const secondRow = worksheet.getRow(2);

                firstRow.values = ['Threshold Value', threshold ?? 0.5];
                secondRow.values = ['', '']
                firstRow.getCell(2).font = { bold: true };
                [firstRow, secondRow].forEach(row => {
                    row.fill = HEADER_ROW_FILL;
                    row.height = 16;
                })
                
                secondRow.border = { bottom: { style: 'thick', color: { argb: 'FF000000' } } }
                startingRowIndex = 3;
            };

            const startingRow = worksheet.getRow(startingRowIndex);
            startingRow.height = 24;

            let dsAndModelHeaders = metricsHeaders.filter(header => !['structure/SMILES', 'smiles'].includes(header.value)).concat(modelHeaders);
            dsAndModelHeaders = dsAndModelHeaders.filter(header => {
                let isDSorModelHeader = true;
                ['chemical-name', 'record-id'].forEach(metadataKey => {
                    if (header.value.includes(metadataKey)) {
                        isDSorModelHeader = false;
                    }
                });
                return isDSorModelHeader;
            });

            const moleculeInfoHeaders = metricsHeaders.filter(header => {
                const matchingHeader = dsAndModelHeaders.find(h => h.value === header.value);
                if (matchingHeader) return false;
                return true;
            });

            [
                {
                    label: 'Molecule Information',
                    startCol: 1,
                    endCol: 1 + moleculeInfoHeaders.length - 1,
                    length: moleculeInfoHeaders.length,
                },
                {
                    label: modelFamilyName,
                    startCol: moleculeInfoHeaders.length + 1, 
                    endCol: moleculeInfoHeaders.length + dsAndModelHeaders.length,
                    length: dsAndModelHeaders.length,
                },
                {
                    label: 'Molecule Highlighting',
                    startCol: moleculeInfoHeaders.length + dsAndModelHeaders.length + 1,
                    endCol: moleculeInfoHeaders.length + dsAndModelHeaders.length + moleculeHighlightingHeaders.length , 
                    length: moleculeHighlightingHeaders.length,
                }
            ].forEach((header, i) => {
                const { label, startCol, endCol, length } = header;

                if (length > 0) {
                    worksheet.mergeCells(startingRowIndex, startCol, startingRowIndex, endCol);
                    const cell = startingRow.getCell(startCol);
                    cell.value = label;
                    cell.font = { bold: true };
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };

                    if (i%2 !== 0) {
                        cell.fill = DARK_CELL_FILL;
                        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                    }
                }
   
            });

            const secondRow = worksheet.getRow(startingRowIndex + 1);
            secondRow.height = 16;
            secondRow.border = { bottom: { style: 'thin', color: { argb: 'FF000000' } } };

            allHeaders.forEach((header,i) => {
                const cell = secondRow.getCell(i+1);
                cell.value = header.label;
                cell.font = { bold: true };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            });

            worksheet.views = [
                { state: 'frozen', ySplit: startingRowIndex+1 },
            ];

            data.forEach((dataRow, dataRowIndex) => {
                const fields = dataRow.fields;
                const rowIndex = dataRowIndex + 2 + startingRowIndex;

                const row = worksheet.getRow(rowIndex);
                row.height = ROW_HEIGHT;

                fields.forEach((field, i) => {
                    const { type, value, associatedColumn } = field;

                    const cell = row.getCell(i+1);
                    cell.alignment = {
                        vertical: 'middle',
                        horizontal: 'center',
                    };

                    if (type === 'structure') {
                        cell.value = '';
                        const dataUrl = convertStructureImagesToBase64(value as string);
                        if (dataUrl) {
                            const imageToAdd = workbook.addImage({
                                base64: dataUrl,
                                extension: 'png',
                            });

                            worksheet.addImage(imageToAdd, {
                                tl: { col: i, row: rowIndex-0.9 },
                                ext: { width: 126, height: 126 },
                            });
                        }
                    } else if (type === 'boolean') {
                        cell.border = {
                            top: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                            bottom: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                            left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                            right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                        }
                        if ((value as unknown as number) === 0) {
                            cell.value = 'False';
                            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF8484' } }
                        } else {
                            cell.value = 'True';
                            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7BC960' } }
                        }
                    } else if (type === 'stat' && !moleculeHighlightingHeaders.find(header => header.value === associatedColumn)) {
                        let cellColor = '';
                        cell.border = {
                            top: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                            bottom: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                            left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                            right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                        }

                        if (Array.isArray(value)) {
                            const { label, color } = getConformalValue(value as number[], threshold ?? 0.5);
                            cellColor = color;
                            cell.value = label;
                        } else {
                            cellColor = getStatsCellColor({ min: 0, max: 1 }, Number(value), false);
                            cell.value = convertNumberToDecimal(value.toString());
                        }
                        
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${cellColor.replace('#','')}` } }
                    } else if (moleculeHighlightingHeaders.find(header => header.value === associatedColumn)) {
                        cell.value = '';
                        const structureField = fields.find(f => f.type === 'structure');
                        if (structureField) {
                            const dataUrl = convertHighlightedStructureImagesToBase64(structureField.value as string, value as unknown as any);
                            if (dataUrl) {
                                const imageToAdd = workbook.addImage({
                                    base64: dataUrl,
                                    extension: 'png',
                                });

                                worksheet.addImage(imageToAdd, {
                                    tl: { col: i+0.1, row: rowIndex-0.9 },
                                    ext: { width: 126, height: 126 },
                                });
                            }
                        }
                    } else {
                        cell.value = value as string;
                    }
                });
            });

            const { headerData, allPredMetadata } = await formatBatchPredictionMetadata(
                predictions,
                modelFamilyName,
                modelHeaders,
                {
                    modelFamilyId,
                    includeModelFamilyDatasetMetadata: XLSX_EXPORT_INCLUDE_MODEL_FAMILY_DATASET_METADATA,
                },
            );

            if (headerData.length > 0 && allPredMetadata.length > 0) {
                const metadataWorksheet = workbook.addWorksheet(`${groupNum+1} - Metadata ${modelFamilyName}`);
                headerData.forEach((data, i) => {
                    const row = metadataWorksheet.getRow(i+1);
                    row.height = 16;
                    row.values = data;
                    row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };

                    if (i === headerData.length - 1) {
                        row.border = { bottom: { style: 'thick', color: { argb: 'FF000000' } } }
                    }

                    row.eachCell((cell) => {
                        if (cell.value && ['Test File', 'Model Family', 'Predictions Metadata'].includes(cell.value as string)) {
                            cell.font = { bold: true };
                        }
                    });
                })

                const startRow = headerData.length + 1;
                metadataWorksheet.getRow(startRow).height = 24;

                metadataWorksheet.views = [
                    { state: 'frozen', ySplit: startRow },
                ]

                let startCol = 1

                allPredMetadata.forEach((predMetadata, i) => {
                    const { name, data } = predMetadata;

                    let maxCols = 0;
                    data.forEach(d => {
                        if (d.length > maxCols) {
                            maxCols = d.length;
                        }
                    });

                    const firstRow = metadataWorksheet.getRow(startRow);

                    metadataWorksheet.mergeCells(startRow, startCol, startRow, startCol + maxCols);
                    const headerCell = firstRow.getCell(startCol);
                    headerCell.value = name;
                    headerCell.font = { bold: true };
                    headerCell.alignment = { vertical: 'middle', horizontal: 'center' };

                    if (i%2 !== 0) {
                        headerCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B7B7B' } };
                        headerCell.font = { bold: true, color: { argb: 'FFFFFFFF' } }
                    }

                    data.forEach((values, index) => {
                        const row = metadataWorksheet.getRow(startRow + 1 + index);
                        values.forEach((value, valueIndex) => {
                            row.getCell(startCol + valueIndex).value = value;
                        });
                    });

                    let colWidth = 0;
                    const firstCol = metadataWorksheet.getColumn(startCol);
                    firstCol.eachCell((cell) => {
                        const length = cell.value ? cell.value.toString().length : 0;
                        if (length > colWidth) {
                            colWidth = length;
                        }
                    });

                    if (colWidth > 0) {
                        firstCol.width = colWidth;
                    }
                    startCol = startCol + maxCols + 1;

                })
            };

            if (headerData.length > 0 && predEvalMetadata !== undefined && predEvalMetadata.length > 0) {
                await addResultsetEvaluationWorksheet(
                    workbook,
                    modelFamilyId,
                    modelFamilyName,
                    headerData,
                    predEvalMetadata,
                );
            }
        };

        const buffer = await workbook.xlsx.writeBuffer();
        downloadData(resultset.name ?? '', '.xlsx', buffer);
    }

    const addResultsetEvaluationWorksheet = async (
        workbook: ExcelJS.Workbook,
        modelFamilyId: number, 
        modelFamilyName: string,
        headerData: string[][], 
        predEvalMetadata: ResultsetEvaluationMetadata[],
    ) => {
        const modelFamilyPredEvalMetadata = predEvalMetadata.filter(data => Number(data.modelFamilyId) === modelFamilyId);
        if (modelFamilyPredEvalMetadata.length > 0) {
            const { evalData, evalHeaders } = formatResultsetValidationsData(modelFamilyPredEvalMetadata);

            if (evalData.length > 0 && evalHeaders.length > 0) {
                const evalWorksheet = workbook.addWorksheet(`Model Validation Data ${modelFamilyName}`);

                evalWorksheet.columns = Array.from(Array(16).keys()).map(key => ({ width: 22 }));

                headerData.forEach((data, i) => {
                    const row = evalWorksheet.getRow(i+1);
                    row.height = 16;

                    const updatedValues = data.map(d => {
                        if (d === 'Predictions Metadata') {
                            return 'Model Validations Data';
                        } else {
                            return d;
                        }
                    });
                    row.values = updatedValues;
                    row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };

                    if (i === headerData.length - 1) {
                        row.border = { bottom: { style: 'thick', color: { argb: 'FF000000' } } }
                    }

                    row.eachCell((cell) => {
                        if (cell.value && ['Test File', 'Model Family', 'Model Validations Data'].includes(cell.value as string)) {
                            cell.font = { bold: true };
                        }
                    });
                })

                const statsHeadingRowIndex = headerData.length + 1;
                const statsHeadingRow = evalWorksheet.getRow(statsHeadingRowIndex);
                statsHeadingRow.height = 16;

                statsHeadingRow.getCell(1).value = 'Method Name';
                const representativeRow = evalData[0].fields.filter(field => field.type !== 'plot' && field.associatedColumn !== 'method');
                evalWorksheet.mergeCells(statsHeadingRowIndex, 2, statsHeadingRowIndex, 2+representativeRow.length-1);
                statsHeadingRow.getCell(2).value = 'Statistics';

                [1,2].forEach((cellNum) => {
                    const cell = statsHeadingRow.getCell(cellNum);
                    cell.font = { bold: true };
                    cell.alignment = { vertical: 'middle', horizontal: 'left' }
                    if (cellNum === 2) {
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF7B7B7B' } };
                        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                        cell.alignment = { vertical: 'middle', horizontal: 'center' };
                    }
                });

                const statsRow = evalWorksheet.getRow(statsHeadingRowIndex+1);
                statsRow.values = [''].concat(representativeRow.map(rep => rep.associatedColumn));
                statsRow.font = { bold: true };
                statsRow.alignment = { vertical: 'middle', horizontal: 'center' };

                const dataRowIndex = statsHeadingRowIndex+2;

                evalData.forEach((modelData, i) => {
                    const modelDataRow = evalWorksheet.getRow(dataRowIndex+i);
                    modelDataRow.height = 16;
                    const fields = modelData.fields.filter(field => field.type !== 'plot');
                    fields.forEach((field, cellIndex) => {
                        const { value, associatedColumn, type } = field;
                        const cell = modelDataRow.getCell(cellIndex+1);
                        if (type === 'stat') {
                            cell.value = convertNumberToDecimal(value as string | number);
                            cell.alignment = { vertical: 'middle', horizontal: 'center' };

                            const modelLimit = modelStatLimits.find(limit => limit.value === associatedColumn);
                            let color = '#E0E0E0';
                            if (modelLimit) {
                                color = getStatsCellColor({ min: modelLimit.min, max: modelLimit.max }, Number(value), false);
                            } 

                            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${color.split('#')[1]}` } };
                        } else {
                            cell.value = value as string | number;
                            cell.alignment = { vertical: 'middle', horizontal: associatedColumn === 'method' ? 'left' : 'center' };
                        }
                    })
                });

                const plotRowIndex = dataRowIndex + evalData.length;

                for (const [i, modelData] of Object.entries(evalData)) {
                    const rowIndex = Number(i) + plotRowIndex + 1;

                    const fields = modelData.fields.filter(field => field.type === 'plot');
                    const row = evalWorksheet.getRow(rowIndex);
                    row.height = 320;

                    const nameField = modelData.fields.find(field => field.associatedColumn === 'method');
                    if (nameField) {
                        const nameCell = row.getCell(1);
                        nameCell.value = nameField.value as string;
                        nameCell.font = { bold: true };
                        nameCell.alignment = { vertical: 'middle', horizontal: 'left' }; 
                    }

                    const plotCells: {
                        startCol: number,
                        endCol: number,
                        plotField: TableDataField,
                    }[] = fields.map((field, fieldIndex) => {
                        let startCol = 0;
                        let endCol = 0

                        if (fieldIndex === 0) {
                            startCol = 2;
                            endCol = 4;
                        } else if (fieldIndex === 1) {
                            startCol = 5;
                            endCol = 7;
                        } else {
                            startCol = 8;
                            endCol = 10;
                        }

                        return {
                            startCol,
                            endCol,
                            plotField: field,
                        }
                    });

                    for (const plotCell of plotCells) {
                        const { startCol, endCol, plotField } = plotCell;
                        evalWorksheet.mergeCells(rowIndex, startCol, rowIndex, endCol);

                        let plotData = plotField.value;
                        let layout = null;

                        if (plotField.associatedColumn === 'roc') {
                            layout = rocLayout;
                        } else if (plotField.associatedColumn === 'overlay') {
                            layout = modelOverlayLayout;
                        } else if (plotField.associatedColumn === 'truthTable') {
                            const formattedData = formatTruthTableData(plotField.value as any[]);
                            plotData = formattedData.traces;
                            layout = formattedData.layout;
                        } else {
                            layout = scatterPlotLayout(evalData as any[]);
                        };

                        const url = await Plotly.toImage({
                            data: plotData as any[],
                            layout: layout as any,
                        }, { width: 500, height: 500, format: 'png' });

                        if (url) {
                            const imageToAdd = workbook.addImage({
                                base64: url,
                                extension: 'png',
                            });

                            evalWorksheet.addImage(imageToAdd, {
                                tl: { col: startCol-1+0.1, row: rowIndex-1+0.1 },
                                ext: { width: 500, height: 500 },
                            });
                        }
                    }
                };
            }
        }  
    }

    const createVisualizationExcelFile = async (
        name: string,
        visualization: ResultsetVisualizationType,
        resultset: Resultset,
        visData: {
            id: string | number,
            label: string,
            structure_column: string,
            set: 'train' | 'test',
            set_name: string,
            data: ResultsetVisualizationDownloadFormat[],
        }[],
    ) => {
        const ROW_HEIGHT = 107;
        const COL_WIDTH = 16;

        const workbook = new ExcelJS.Workbook();

        const visualizationMethodMetadata = visualization.metadata.dataset_visualization_metadata.visualization_method_metadata;
        const { method_type, params } = visualizationMethodMetadata;

        visData.forEach((vis, visIndex) => {
            const { label, structure_column, data: set_data, set, set_name } = vis;
            const worksheet = workbook.addWorksheet(`${visIndex+1} - ${label}`);

            const headingRows: (string | number | boolean)[][] = [
                ['Set Type', set],
                ['Set Name', set_name],
                ['Structure Column', structure_column],
                [''],
                ['Method Type', method_type],
            ];

            for (const [paramKey, paramValue] of Object.entries(params)) {
                headingRows.push([paramKey, paramValue]);
            }

            headingRows.forEach((headingRow, i) => {
                const row = worksheet.getRow(i+1);
                row.values = headingRow;
                row.height = 16;
                row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
                row.getCell(1).font = { bold: true };

                if (i === headingRows.length-1) {
                    row.border = { bottom: { style: 'thick', color: { argb: 'FF000000' } } }
                };
            });

            const headerRow = worksheet.getRow(headingRows.length+1);
            headerRow.height = 16;
            
            if (set === 'train' && set_data[0].structure === undefined) {
                headerRow.values = ['x', 'y'];
                [1,2].forEach(colNum => {
                    worksheet.getColumn(colNum).width = 16;
                })
            } else {
                headerRow.values = ['Structure', 'SMILES', 'x', 'y'];
                [COL_WIDTH, 24, 16, 16].forEach((colWidth, i) => {
                    worksheet.getColumn(i+1).width = colWidth;
                });
            }

            worksheet.views = [
                { state: 'frozen', ySplit: headingRows.length+1 },
            ];

            const startingRowIndex = headingRows.length+2;

            set_data.forEach((data, i) => {
                const rowIndex = i+startingRowIndex
                const row = worksheet.getRow(rowIndex);
                if (data.structure === undefined) {
                    row.height = 16;
                } else {
                    row.height = ROW_HEIGHT;
                }

                for (const [index, [key, value]] of Object.entries(data).entries()) {
                    if (value !== undefined) {
                        const cell = row.getCell(Number(index)+1);
                        if (key === 'structure') {
                            cell.value = '';
                            const dataUrl = convertStructureImagesToBase64(value as string);
                            if (dataUrl) {
                                const imageToAdd = workbook.addImage({
                                    base64: dataUrl,
                                    extension: 'png',
                                });

                                worksheet.addImage(imageToAdd, {
                                    tl: { col: index, row: rowIndex-0.9 },
                                    ext: { width: 126, height: 126 },
                                });
                            }
                        } else {
                            cell.value = value;
                            cell.alignment = { vertical: 'middle', horizontal: 'center' };
                        }
                    }
                }
            })
        });

        const { headingRows: metadataHeadingRows, metadataRows } = formatResultsetVisualizationMetadata(visualization, resultset);

        if (metadataHeadingRows.length > 0 && metadataRows.length > 0) {
            const metadataWorksheet = workbook.addWorksheet('Resultset Visualization Metadata');

            metadataHeadingRows.forEach((metadataHeadingRow, rowIndex) => {
                const row = metadataWorksheet.getRow(rowIndex+1);
                row.values = metadataHeadingRow;
                row.height = 16;
                row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
                row.getCell(1).font = { bold: true };

                if (rowIndex === metadataHeadingRow.length-1) {
                    row.border = { bottom: { style: 'thick', color: { argb: 'FF000000' } } }
                };
            });

            const metadataStartIndex = metadataHeadingRows.length + 1;
            metadataRows.forEach((metadataRow, rowIndex) => {
                const row = metadataWorksheet.getRow(metadataStartIndex+rowIndex);
                row.height = 16;
                row.values = metadataRow;
                row.eachCell((cell) => {
                    if (cell.value && cell.value === 'Fingerprint Metadata') {
                        cell.font = { bold: true }
                    }
                })
            });

            let colWidth = 0;
            const firstCol = metadataWorksheet.getColumn(1);

            firstCol.eachCell((cell) => {
                if (cell.value) {
                    const length = cell.value.toLocaleString().length;
                    if (length > colWidth) {
                        colWidth = length;
                    }
                }
            });

            firstCol.width = colWidth;
        }

        const buffer = await workbook.xlsx.writeBuffer();
        downloadData(name, '.xlsx', buffer);

    }

    const createDatasetProtocolExcelFile = async (dataset: Dataset, protocol: Protocol) => {
        const { protocol_metadata } = protocol;
        const { stage_changes } = protocol_metadata ?? ({ stage_changes: [], total_changes: [] } as { stage_changes: ProtocolMetadataOutputStageChanges[], total_changes: ProtocolMetadataOutputTotalChanges[] });

        const STRUCTURE_ROW_HEIGHT = 107;
        const ROW_HEIGHT = 16;
        const COL_WIDTH = 16;

        const workbook = new ExcelJS.Workbook();

        if (stage_changes && stage_changes.length > 0) {
            for (const [change_idx, change] of (stage_changes as StageChanges[]).entries()) {
                const { function: stage_function, standardization_records } = change;

                const worksheet = workbook.addWorksheet(stage_function ?? `Stage ${change_idx+1}`);
                worksheet.views = [
                    { state: 'frozen', ySplit: 2, xSplit: 1 },
                ]

                const test_record = standardization_records && standardization_records.length > 0 ? standardization_records[0] : null;
                let structureValue = '';
                const transformedFields: string[] = [];

                if (test_record) {
                    const { transformed_fields } = test_record;
                    if (transformed_fields) {
                        const structureKey = Object.keys(transformed_fields).find(field => field.toLocaleLowerCase().includes('smile'));
                        if (structureKey) {
                            structureValue = structureKey;
                        }
                        Object.keys(transformed_fields).forEach(field => transformedFields.push(field));
                    }
                }
                
                Array.from((Array(6 + (transformedFields.length*2)).keys())).forEach(key => {
                    worksheet.getColumn(key+1).width = COL_WIDTH;
                })

                const headerRowValues = [
                    { label: 'Structure', value: structureValue, rowspan: 2, cellIndex: 1 },
                    { label: 'Ordinal', value: 'ordinal', rowspan: 2, cellIndex: 2 },
                    { label: 'Result', value: 'result', rowspan: 2, cellIndex: 3 },
                    { label: 'Severity', value: 'severity', rowspan: 2, cellIndex: 4 },
                    { label: 'Reason', value: 'reason', rowspan: 2, cellIndex: 5 },
                    { label: 'Original Fields', value: 'original_fields', colspan: transformedFields.length, cellIndex: 6 },
                    { label: 'Transformed Fields', value: 'transformed_fields', colspan: transformedFields.length, cellIndex: transformedFields.length > 0 ? 6 + transformedFields.length : 7 }
                ]

                const headerRowOne = worksheet.getRow(1);
                headerRowOne.font = { bold: true };
                headerRowOne.alignment = { vertical: 'middle', horizontal: 'center' }
                headerRowOne.height = ROW_HEIGHT;
                headerRowOne.fill = HEADER_ROW_FILL;
                headerRowOne.border = {
                    bottom: { color: { argb: '00000000' }, style: 'thin' }
                }

                headerRowValues.forEach((headerRowValue) => {
                    const cell = headerRowOne.getCell(headerRowValue.cellIndex);
                    cell.value = headerRowValue.label;
                    cell.alignment = { vertical: 'middle', horizontal: 'left' }

                    if (headerRowValue.rowspan) {
                        worksheet.mergeCells(1, headerRowValue.cellIndex, 2, headerRowValue.cellIndex);
                    }

                    if (headerRowValue.colspan) {
                        cell.alignment = { vertical: 'middle', horizontal: 'center' }
                        worksheet.mergeCells(1, headerRowValue.cellIndex, 1, (headerRowValue.cellIndex + headerRowValue.colspan)-1);
                    }
                })

                const headerRowTwo = worksheet.getRow(2);
                headerRowTwo.font = { bold: true };
                headerRowTwo.alignment = { vertical: 'middle', horizontal: 'center' }
                headerRowTwo.height = ROW_HEIGHT;
                headerRowTwo.fill = HEADER_ROW_FILL;
                headerRowTwo.border = {
                    bottom: { color: { argb: '00000000' }, style: 'thin' }
                };

                transformedFields.forEach((field, fieldIndex) => {
                    const cellOne = headerRowTwo.getCell(6 + fieldIndex);
                    const cellTwo = headerRowTwo.getCell(6 + fieldIndex + transformedFields.length);

                    cellOne.value = field;
                    cellOne.alignment = { vertical: 'middle', horizontal: 'left'}
                    cellTwo.value = field;
                    cellTwo.alignment = { vertical: 'middle', horizontal: 'left'}
                });

                const startingRowIndex = 3;

                if (standardization_records && standardization_records.length > 0) {
                    standardization_records.forEach((record, recordIndex) => {
                        const row = worksheet.getRow(startingRowIndex + recordIndex);
                        row.alignment = { vertical: 'middle', horizontal: 'left' }
                        row.height = STRUCTURE_ROW_HEIGHT;
                        
                        headerRowValues.forEach(headerRowValue => {
                            const { label, value, cellIndex } = headerRowValue;

                            if (value !== 'original_fields' && value !== 'transformed_fields') {
                                const cell = row.getCell(cellIndex);

                                if (label === 'Structure') {
                                    const cellValue = record.transformed_fields ? record.transformed_fields[value as keyof object] : '';
                                    cell.value = '';
                                    const dataUrl = convertStructureImagesToBase64(cellValue as string ?? '');
                                    if (dataUrl) {
                                        const imageToAdd = workbook.addImage({
                                            base64: dataUrl,
                                            extension: 'png',
                                        });

                                        worksheet.addImage(imageToAdd, {
                                            tl: { col: 0, row: Number(startingRowIndex + recordIndex)-0.9 },
                                            ext: { width: 126, height: 126 },
                                        });
                                    }
                                } else {
                                    const cellValue = record[value as keyof object];
                                    cell.value = cellValue;
                                }
                            } else if (transformedFields.length > 0 && record[value as keyof object]) {
                                transformedFields.forEach((field, fieldIndex) => {
                                    const cell = row.getCell(cellIndex + fieldIndex);
                                    const cellValue = record[value as keyof object][field as keyof object];
                                    cell.value = cellValue;
                                })
                            }
                        })
                    })
                }
                
            };

            const metadataWorksheet = workbook.addWorksheet('Protocol Metadata');

            Array.from(Array(5).keys()).forEach(key => metadataWorksheet.getColumn(key+1).width = COL_WIDTH);

            const metadataRows = formatProtocolStageMetadata(dataset, protocol);
            metadataRows.forEach((metadataRow, i) => {
                const row = metadataWorksheet.getRow(i+1);
                row.values = metadataRow;
                row.getCell(1).font = { bold: true }
            })

        };

        const buffer = await workbook.xlsx.writeBuffer();
        downloadData(dataset.name ?? '', '.xlsx', buffer);
    }


    const createDatasetExcelFile = async (dataset: Dataset, dataframe: { [key: string]: { [key: number]: string | number | null } }) => {
        const ROW_HEIGHT = 107;
        const COL_WIDTH = 16;

        const workbook = new ExcelJS.Workbook();

        const worksheet = workbook.addWorksheet('Dataset Data');
        worksheet.views = [
            { state: 'frozen', ySplit: 1 }
        ];

        const { dsHeaders, dsData } = formatDatasetDataframe(dataframe);

        const cols: Partial<ExcelJS.Column>[] = [];

        dsHeaders.forEach((header, i) => {
            let width = COL_WIDTH;

            if (i > 0) {
                if (i === 1) {
                    width = 28;
                } else {
                    width = header.label.length + 4;
                }
            }

            cols.push({
                header: header.label,
                width,
            });
        });

        worksheet.columns = cols;

        const firstRow = worksheet.getRow(1);
        firstRow.font = { bold: true };
        firstRow.alignment = { vertical: 'middle', horizontal: 'center' }
        firstRow.border = {
            bottom: { color: { argb: '00000000' }, style: 'thin' }
        }
        firstRow.height = 16;

        dsData.forEach((data, dataIndex) => {
            const { fields } = data;

            const row = worksheet.getRow(dataIndex+2);
            row.height = ROW_HEIGHT;
            
            fields.forEach((field, fieldIndex) => {
                const cell = row.getCell(fieldIndex+1);
                if (field.type === 'structure') {
                    cell.value = '';
                    const dataUrl = convertStructureImagesToBase64(field.value as string);
                    if (dataUrl) {
                        const imageToAdd = workbook.addImage({
                            base64: dataUrl,
                            extension: 'png',
                        });

                        worksheet.addImage(imageToAdd, {
                            tl: { col: fieldIndex, row: Number(dataIndex)+1.1 },
                            ext: { width: 126, height: 126 },
                        });
                    }
                } else {
                    cell.value = field.value as string | number;
                }

                cell.alignment = {
                    vertical: 'middle',
                    horizontal: 'center',
                }
            })
        });

        const metadata = await formatDatasetMetadata(dataset);

        if (metadata.length > 0) {
            const metadataWorksheet = workbook.addWorksheet('Dataset Metadata');
            metadata.forEach((data, i) => {
                const row = metadataWorksheet.getRow(i+1);
                row.values = data;
                row.height = 16;

                if (i <= 3) {
                    row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
                    if (i === 3) {
                        row.border = { bottom: { style: 'thick', color: { argb: 'FF000000' } } };
                    }
                }

                row.eachCell((cell) => {
                    if (cell.value && ['Parent Datafile Name', 'Dataset Metadata', 'Column Metadata', 'Stages'].includes(cell.value.toString())) {
                        cell.font = { bold: true };
                    }
                })
            });

            let colWidth = 0;

            const firstColumn = metadataWorksheet.getColumn(1)

            firstColumn.eachCell((cell) => {
                const length = cell.value ? cell.value.toString().length : 0;
                if (length > colWidth) {
                    colWidth = length;
                }
            });

            firstColumn.width = colWidth;
        }

        const buffer = await workbook.xlsx.writeBuffer();
        downloadData(`${dataset.name ?? ''} Protocol`, '.xlsx', buffer);
    }

    // const createReadacrossExcelFile = async (readacross: ReadAcross, data: ReadAcrossDataType[]) => {
    //     // const ROW_HEIGHT = 107;
    //     // const COL_WIDTH = 16;

    //     const ROW_HEIGHT = 167;
    //     const COL_WIDTH = 25;

    //     const workbook = new ExcelJS.Workbook();
    //     const worksheet = workbook.addWorksheet(`Readacross Data`);
    //     worksheet.views = [
    //         { state: 'frozen', ySplit: 4, xSplit: 1 },
    //     ]

    //     worksheet.columns = Array.from(Array(data.length+1).keys()).map(key => ({ width: COL_WIDTH }));

    //     ['Structure', 'SMILES', 'Chemical Name', 'Tanimoto'].forEach((label, i) => {
    //         const cell = worksheet.getRow(i+1).getCell(1);
    //         cell.value = label;
    //         cell.font = { bold: true };
    //         cell.alignment = { vertical: 'middle', horizontal: 'left' };
    //         cell.border = {
    //             top: { style: 'thin', color: { argb: 'FFC1C7CD' } },
    //             bottom: { style: 'thin', color: { argb: 'FFC1C7CD' } },
    //             left: { style: 'thin', color: { argb: 'FFC1C7CD' } },
    //             right: { style: 'thin', color: { argb: 'FFC1C7CD' } },
    //         }
    //     });

    //     const structureRow = worksheet.getRow(1);
    //     structureRow.height = ROW_HEIGHT;

    //     for (const [index, raData] of Object.entries(data)) {
    //         const { chemical_name, smiles, tanimoto } = raData;
    //         const colIndex = Number(index) + 2;

    //         const dataUrl = convertStructureImagesToBase64(smiles);
    //         if (dataUrl) {
    //             const imageToAdd = workbook.addImage({
    //                 base64: dataUrl,
    //                 extension: 'png',
    //             });

    //             worksheet.addImage(imageToAdd, {
    //                 tl: { col: colIndex-1, row: 0.1 },
    //                 ext: { width: 196, height: 196 },
    //             });
    //         }

    //         const structureCell = worksheet.getRow(1).getCell(colIndex);
    //         const smilesCell = worksheet.getRow(2).getCell(colIndex);
    //         smilesCell.value = smiles;

    //         const chemicalNameCell = worksheet.getRow(3).getCell(colIndex);
    //         chemicalNameCell.value = chemical_name;

    //         const tanimotoCell = worksheet.getRow(4).getCell(colIndex);
    //         tanimotoCell.value = convertNumberToDecimal(tanimoto);

    //         [structureCell, smilesCell, chemicalNameCell, tanimotoCell].forEach(cell => {
    //             cell.alignment = { vertical: 'middle', horizontal: 'center' };
    //             cell.border = {
    //                 top: { style: 'thin', color: { argb: 'FFC1C7CD' } },
    //                 bottom: { style: 'thin', color: { argb: 'FFC1C7CD' } },
    //                 left: { style: 'thin', color: { argb: 'FFC1C7CD' } },
    //                 right: { style: 'thin', color: { argb: 'FFC1C7CD' } },
    //             }
    //         })
    //     }

    //     [1,2,3,4].forEach(rowNum => {
    //         const row = worksheet.getRow(rowNum);
    //         if (rowNum > 1) {
    //             row.height = 16;
    //         }
    //         row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F7F7' } };
    //     });

    //     const dataRowIndex = 5;

    //     const toxKeys = Object.keys(data[0].toxicology_data);
    //     toxKeys.forEach((key,i) => {
    //         const row = worksheet.getRow(i+dataRowIndex);
    //         row.height = 20;

    //         const labelCell = row.getCell(1);
    //         labelCell.value = key;
    //         labelCell.alignment = { vertical: 'middle', horizontal: 'left' }
    //         labelCell.font = { bold: true };

    //         data.forEach((colData, colIndex) => {
    //             const { toxicology_data } = colData;
    //             const toxValue = toxicology_data[key];

    //             const toxCell = row.getCell(2+colIndex);
    //             toxCell.value = '';
    //             toxCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: toxValue === 0 ? 'FF7BC960' : 'FFFF8484' } }
    //             toxCell.border = {
    //                 top: { style: 'thin', color: { argb: 'FFFFFFFF' } },
    //                 bottom: { style: 'thin', color: { argb: 'FFFFFFFF' } },
    //                 left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
    //                 right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
    //             }
    //         })
    //     });

    //     const { headingData, data: raMetadata } = await formatReadacrossMetadata(readacross);

    //     if (headingData.length > 0 && raMetadata.length > 0) {
    //         const metadataWorksheet = workbook.addWorksheet('Readacross Metadata');
    //         headingData.forEach((d, i) => {
    //             const row = metadataWorksheet.getRow(i+1);
    //             row.values = d;
    //             row.height = 16;
    //             row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F7F7' } };
                
    //             if (i === headingData.length-1) {
    //                 row.border = { bottom: { style: 'thick', color: { argb: 'FF000000' } } }
    //             }

    //             row.eachCell((cell) => {
    //                 const boldLabels = ['Readacross File Name', 'Readacross File ID', 'Readacross Type', 'Input SMILES', 'Dataset Name', 'Readacross Metadata'];
    //                 if (cell.value && boldLabels.includes(cell.value as string)) {
    //                     cell.font = { bold: true };
    //                 };
    //             });
    //         });

    //         raMetadata.forEach((d,i) => {
    //             const row = metadataWorksheet.getRow(i+1+headingData.length);
    //             row.values = d;
    //             row.height = 16;

    //             row.eachCell((cell) => {
    //                 if (cell.value && cell.value === 'Fingerprint Metadata') {
    //                     cell.font = { bold: true };
    //                 }
    //             });
    //         });

    //         let colWidth = 0;

    //         const firstColumn = metadataWorksheet.getColumn(1)

    //         firstColumn.eachCell((cell) => {
    //             const length = cell.value ? cell.value.toString().length : 0;
    //             if (length > colWidth) {
    //                 colWidth = length;
    //             }
    //         });

    //         firstColumn.width = colWidth;
    //     }

    //     const buffer = await workbook.xlsx.writeBuffer();
    //     downloadData(readacross.name ?? '', '.xlsx', buffer);

    // }

    const createReadacrossExcelFile = async (readacross: ReadAcross, data: ReadAcrossDataType[]) => {
        const ROW_HEIGHT = 167;
        const COL_WIDTH = 25;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(`Readacross Data`);

        worksheet.views = [
            { state: 'frozen', ySplit: 6, xSplit: 1 },
        ]

        // --- Detect old vs. new data ---
        const isOld = !!data[0].toxicology_data && Object.keys(data[0].toxicology_data).length > 0;

        // --- Get toxKeys ---
        const toxKeys = isOld
            ? Object.keys(data[0].toxicology_data)
            : Object.keys(data[0].reg_pred ?? {});

        // --- Set up columns ---
        let numStatCols = 7;
        let numCompounds = data.length;
        let numCols = isOld ? numCompounds + 1 : 1 + numStatCols + (numCompounds - 1);

        worksheet.columns = Array.from(Array(numCols).keys()).map(() => ({ width: COL_WIDTH }));

        // --- Top rows: Structure, SMILES, Chemical Name, Tanimoto ---
        const topLabels = ['Structure', 'SMILES', 'Chemical Name', 'Tanimoto'];
        topLabels.forEach((label, i) => {
            const row = worksheet.getRow(i + 1);
            row.height = i === 0 ? ROW_HEIGHT : 16;
            row.getCell(1).value = label;
            row.getCell(1).font = { bold: true };
            row.getCell(1).alignment = { vertical: 'middle', horizontal: 'left' };
            row.getCell(1).border = {
                top: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                bottom: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                left: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                right: { style: 'thin', color: { argb: 'FFC1C7CD' } },
            };
            row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F7F7' } };
        });

        // --- Fill in structure images and metadata for each compound ---
        for (const [index, raData] of Object.entries(data)) {
            const { chemical_name, smiles, tanimoto } = raData;
            let colIndex;
            if (isOld) {
                colIndex = Number(index) + 2;
            } else {
                colIndex = Number(index) === 0 ? 2 : 2 + numStatCols + (Number(index) - 1);
            }

            // Structure image
            const dataUrl = convertStructureImagesToBase64(smiles);
            if (dataUrl) {
                const imageToAdd = workbook.addImage({
                    base64: dataUrl,
                    extension: 'png',
                });
                worksheet.addImage(imageToAdd, {
                    tl: { col: colIndex - 1, row: 0.1 },
                    ext: { width: 196, height: 196 },
                });
            }

            // SMILES, Chemical Name, Tanimoto
            worksheet.getRow(2).getCell(colIndex).value = smiles;
            worksheet.getRow(3).getCell(colIndex).value = chemical_name;
            worksheet.getRow(4).getCell(colIndex).value = convertNumberToDecimal(tanimoto);

            [1, 2, 3, 4].forEach(rowNum => {
                const cell = worksheet.getRow(rowNum).getCell(colIndex);
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                    bottom: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                    left: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                    right: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                };
            });
        }

        // --- Merge cells for new data format ---
        if (!isOld) {
            // Merge the first 7 columns for the target compound in the top 4 rows
            for (let rowNum = 1; rowNum <= 4; rowNum++) {
                worksheet.mergeCells(rowNum, 2, rowNum, 2 + numStatCols - 1);
            }
        }

        // --- Data rows ---
        if (isOld) {
            // --- Old logic (keep as is) ---
            const dataRowIndex = 5;
            toxKeys.forEach((key, i) => {
                const row = worksheet.getRow(i + dataRowIndex);
                row.height = 20;

                const labelCell = row.getCell(1);
                labelCell.value = key;
                labelCell.alignment = { vertical: 'middle', horizontal: 'left' }
                labelCell.font = { bold: true };

                data.forEach((colData, colIndex) => {
                    const { toxicology_data } = colData;
                    const toxValue = toxicology_data[key];

                    const toxCell = row.getCell(2 + colIndex);
                    toxCell.value = '';
                    toxCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: toxValue === 0 ? 'FF7BC960' : 'FFFF8484' } }
                    toxCell.border = {
                        top: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                        bottom: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                        left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                        right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                    }
                })
            });
        } else {
            // --- New logic ---
            const statNames = [
                "True Value",
                "Confusion Label",
                "Predicted Classification",
                "ACT",
                "AUC",
                "p-value",
                "t0"
            ];

            // Stat header row (row 5): merge for target, then one per analog
            worksheet.mergeCells(5, 2, 5, 2 + numStatCols - 1);
            worksheet.getRow(5).getCell(2).value = data[0].chemical_name || "Target Compound Stats";
            worksheet.getRow(5).getCell(2).font = { bold: true };
            worksheet.getRow(5).getCell(2).alignment = { vertical: 'middle', horizontal: 'center' };

            // Analog headers
            data.slice(1).forEach((colData, idx) => {
                const colIndex = 2 + numStatCols + idx;
                const cell = worksheet.getRow(5).getCell(colIndex);
                cell.value = colData.chemical_name || `Compound ${idx + 2}`;
                cell.font = { bold: true };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.fill = HEADER_ROW_FILL;
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                    bottom: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                    left: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                    right: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                }
            });

            // Stat sub-header row (row 6)
            const subHeaderRow = worksheet.getRow(6);
            statNames.forEach((stat, i) => {
                const cell = subHeaderRow.getCell(2 + i);
                cell.value = stat;
                cell.font = { bold: true };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.fill = HEADER_ROW_FILL;
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                    bottom: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                    left: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                    right: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                }
            });

            // Data rows
            toxKeys.forEach((toxKey, i) => {
                const rowIndex = i + 7;
                const row = worksheet.getRow(rowIndex);
                row.height = 20;

                // Label cell
                const labelCell = row.getCell(1);
                labelCell.value = toxKey;
                labelCell.alignment = { vertical: 'middle', horizontal: 'left' }
                labelCell.font = { bold: true };

                // --- First compound: 7 stats ---
                const stats = [
                    data[0].true_value?.[toxKey],
                    data[0].confusion_label?.[toxKey],
                    data[0].clf_pred?.[toxKey],
                    data[0].clf_proba?.[toxKey],
                    data[0].auc?.[toxKey],
                    data[0].pval?.[toxKey],
                    data[0].t0?.[toxKey],
                ];
                stats.forEach((stat, j) => {
                    const cell = row.getCell(2 + j);
                    let value = stat;
                    if (value === null || value === undefined || value === "") value = "N/A";
                    if (statNames[j] === "Predicted Classification") {
                        // 0 = No Effect (green), 1 = Active (red)
                        cell.value = value === 0 ? "No Effect" : value === 1 ? "Active" : value;
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: value === 0 ? 'FF7BC960' : value === 1 ? 'FFF87171' : 'FFE0E0E0' } };
                        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                    } else if (statNames[j] === "Confusion Label") {
                        // Second letter: N = green, P = red
                        let color = 'FFE0E0E0';
                        if (typeof value === "string" && value.length > 1) {
                            color = value === "TN" || value === "FP" ? 'FF7BC960' : value === "TP" || value === "FN" ? 'FFF87171' : 'FFE0E0E0';
                        }
                        cell.value = value;
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
                        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                    } else if (statNames[j] === "True Value") {
                        cell.value = value;
                        // Optionally color by value (e.g., green/red for 0/>0)
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: value === "N/A" ? 'FFE0E0E0' : value === 0 ? 'FF7BC960' : 'FFF87171' } };
                    } else{
                        cell.value = value;
                        cell.fill = HEADER_ROW_FILL;
                    }
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                    cell.border = {
                        top: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                        bottom: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                        left: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                        right: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                    }
                });

                // --- Other compounds: true value or "No Data" ---
                data.slice(1).forEach((colData, idx) => {
                    const colIndex = 2 + numStatCols + idx;
                    // const value = colData.true_value?.[toxKey];
                    const value = colData.reg_pred?.[toxKey];
                    const cell = row.getCell(colIndex);
                    if (value === null || value === undefined) {
                        cell.value = "No Data";
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
                        cell.font = { italic: true, color: { argb: 'FF888888' } };
                    } else {
                        cell.value = value;
                        // Optionally color by value (e.g., green/red for 0/>0)
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: value === 0 ? 'FF7BC960' : 'FFF87171' } };
                    }
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                    cell.border = {
                        top: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                        bottom: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                        left: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                        right: { style: 'thin', color: { argb: 'FFC1C7CD' } },
                    }
                });
            });
        }

        // --- Metadata worksheet (unchanged) ---
        const { headingData, data: raMetadata } = await formatReadacrossMetadata(readacross);

        if (headingData.length > 0 && raMetadata.length > 0) {
            const metadataWorksheet = workbook.addWorksheet('Readacross Metadata');
            headingData.forEach((d, i) => {
                const row = metadataWorksheet.getRow(i + 1);
                row.values = d;
                row.height = 16;
                row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F7F7' } };

                if (i === headingData.length - 1) {
                    row.border = { bottom: { style: 'thick', color: { argb: 'FF000000' } } }
                }

                row.eachCell((cell) => {
                    const boldLabels = ['Readacross File Name', 'Readacross File ID', 'Readacross Type', 'Input SMILES', 'Dataset Name', 'Readacross Metadata'];
                    if (cell.value && boldLabels.includes(cell.value as string)) {
                        cell.font = { bold: true };
                    };
                });
            });

            raMetadata.forEach((d, i) => {
                const row = metadataWorksheet.getRow(i + 1 + headingData.length);
                row.values = d;
                row.height = 16;

                row.eachCell((cell) => {
                    if (cell.value && cell.value === 'Fingerprint Metadata') {
                        cell.font = { bold: true };
                    }
                });
            });

            let colWidth = 0;

            const firstColumn = metadataWorksheet.getColumn(1)

            firstColumn.eachCell((cell) => {
                const length = cell.value ? cell.value.toString().length : 0;
                if (length > colWidth) {
                    colWidth = length;
                }
            });

            firstColumn.width = colWidth;
        }

        const buffer = await workbook.xlsx.writeBuffer();
        downloadData(readacross.name ?? '', '.xlsx', buffer);
    };


    const convertStructureImagesToBase64 = (smiles: string) => {
        if (!window.RDKit) return;

        const instance = window.RDKit;
        const mol = instance.get_mol(smiles);

        const canvas = document.createElement("canvas");
        canvas.width = 200;
        canvas.height = 200;

        if (mol) {
            mol.draw_to_canvas(canvas, 200, 200);
            const dataUrl = canvas.toDataURL('image/png');
            return dataUrl;
        }
    }

    const convertHighlightedStructureImagesToBase64 = (
        smiles: string, 
        atomsAndBonds: {
            atoms: {[key: number]: number},
            bonds: {[key: number]: number},
        }
    ) => {
        if (!window.RDKit) return;

        const instance = window.RDKit;

        let highlighted_atoms: number[] | undefined;
        let highlighted_bonds: number[] | undefined;
        let atom_colors = {};
        let bond_colors = {};

        const { atoms, bonds } = atomsAndBonds;

        const atomObj = getAtomOrBondColors(atoms);
        highlighted_atoms = atomObj.atoms_or_bonds;
        atom_colors = atomObj.colors;

        const bondObj = getAtomOrBondColors(bonds);
        highlighted_bonds = bondObj.atoms_or_bonds;
        bond_colors = bondObj.colors;

        const mol = instance.get_mol(smiles);
        const canvas = document.createElement("canvas");
        canvas.width = 200;
        canvas.height = 200;

        if (mol) {
            const mDetails = {
                width: 200,
                height: 200,
                bondLineWidth: 1,
                atoms: highlighted_atoms,
                bonds: highlighted_bonds,
                highlightAtomColors: atom_colors,
                highlightBondColors: bond_colors,
                highlightBondWidthMultiplier: 16,
                addAtomIndices: true,
            };
            mol.draw_to_canvas_with_highlights(canvas, JSON.stringify(mDetails));
            const dataUrl = canvas.toDataURL('image/png');
            return dataUrl;
        }
        
    }

    return {
        downloadDataset,
        downloadBatchPrediction,
        downloadModelStats,
        downloadModelFamilyStats,
        downloadReadacrossData,
        downloadResultsetVisualization,
        downloadDatasetProtocol,
    }

};

export default useDownload;